import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Film, Trash2, Upload } from "lucide-react";
import { authUtils } from "@/utils/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 200 * 1024 * 1024;

export const mediaUrl = (token, id) => `${API}/junior/progress/${token}/media/${id}`;

export const MediaPlayer = ({ token, media, testId }) =>
  media.content_type.startsWith("audio/")
    ? <audio controls preload="metadata" src={mediaUrl(token, media.id)} className="w-full mt-2" data-testid={testId} />
    : <video controls playsInline preload="metadata" src={mediaUrl(token, media.id)} className="w-full max-h-80 mt-2 rounded-lg bg-black" data-testid={testId} />;

export const SessionMedia = ({ student, index, media, onChange, testKey }) => {
  const input = useRef(null);
  const [pct, setPct] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^(video|audio)\//.test(file.type)) return toast.error("Please choose a video or audio file");
    if (file.size > MAX) return toast.error("Clips must be 200 MB or smaller");
    const auth = authUtils.getAuthConfig();
    setPct(0);
    try {
      const init = await axios.post(`${API}/admin/junior/media/init`, { booking_id: student.id, child: student.child, session_index: index, filename: file.name, content_type: file.type, size: file.size }, auth);
      const { upload_id, chunk_size, total_chunks } = init.data;
      for (let i = 0; i < total_chunks; i++) {
        const chunk = file.slice(i * chunk_size, (i + 1) * chunk_size);
        await axios.put(`${API}/admin/junior/media/${upload_id}/chunk/${i}`, chunk, { headers: { ...auth.headers, "Content-Type": "application/octet-stream" } });
        setPct(Math.round(((i + 1) / total_chunks) * 95));
      }
      const done = await axios.post(`${API}/admin/junior/media/${upload_id}/complete`, {}, auth);
      onChange(done.data.media);
      toast.success("Clip attached — parents can watch it on their progress page");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed, please try again");
    }
    setPct(null);
    input.current.value = "";
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/admin/junior/media/${media.id}`, authUtils.getAuthConfig());
      onChange(null);
    } catch {
      toast.error("Couldn't remove clip");
    }
  };

  return (
    <div className="mt-2" data-testid={`junior-session-media-${testKey}-${index}`}>
      <input ref={input} type="file" accept="video/*,audio/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`junior-session-media-input-${testKey}-${index}`} />
      {media && (
        <div className="rounded-lg border border-white/10 p-2">
          <div className="flex items-center justify-between gap-2 text-xs text-white/60">
            <span className="flex items-center gap-1.5 truncate"><Film className="w-3.5 h-3.5 shrink-0" /> {media.filename}</span>
            <button type="button" onClick={remove} className="text-red-400 hover:text-red-300 flex items-center gap-1" data-testid={`junior-session-media-remove-${testKey}-${index}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>
          </div>
          <MediaPlayer token={student.progress_token} media={media} testId={`junior-session-media-player-${testKey}-${index}`} />
        </div>
      )}
      {pct !== null ? (
        <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-2" data-testid={`junior-session-media-progress-${testKey}-${index}`}><div className="h-full bg-lime-300 transition-[width]" style={{ width: `${pct}%` }} /></div>
      ) : (
        <button type="button" onClick={() => input.current.click()} className="mt-2 inline-flex items-center gap-1.5 text-xs text-lime-300 hover:text-white" data-testid={`junior-session-media-upload-${testKey}-${index}`}>
          <Upload className="w-3.5 h-3.5" /> {media ? "Replace clip" : "Attach video or recording (max 200 MB)"}
        </button>
      )}
    </div>
  );
};
